import {Component,EventEmitter,Input,OnChanges,OnDestroy,OnInit,Output,SimpleChanges,TemplateRef,ViewChild} from '@angular/core';
import {FormControl,FormGroupDirective,NgForm,NgModel} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {debounceTime,filter,finalize,map,switchMap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ErrorStateMatcher} from '@angular/material/core';
import {Observable,Subscription} from 'rxjs';
import {MatLegacyInput as MatInput} from '@angular/material/legacy-input';

import {AutocompleteOptions} from './autocomplete';
import {environment} from '@environments/environment';
import {AutocompleteService} from './autocomplete.service';
import {LegacyFloatLabelType as FloatLabelType} from "@angular/material/legacy-form-field";
import {SearchType} from "@domain/common/list-view/sorting";
import {AutocompleteType} from "@share/component/autocomplete/options/autocomplete-type";
import {DatePipe} from "@share/pipe/custom-date.pipe";
import {MontantPipe} from "@share/pipe/montant";
import {MatErrorModel,MatHintModel} from "../custom-input/custom-input.model";

@Component({
    selector: 'autocomplete',
    exportAs: 'autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss']
})
export class AutocompleteComponent implements OnInit,OnDestroy,OnChanges {
    /** Options de l'autocomplete */
    @Input() options: AutocompleteOptions;

    /** Type générique d'autocomplete */
    @Input() type: AutocompleteType;

    /** Filtres de l'autocomplete */
    @Input() filter?: any;

    /** Options supplémentaires de l'autocomplete */
    @Input() extraOptions?: any;

    /** Placeholder */
    @Input() placeholder: string;

    /** Autocomplete à partir d'un custom input */
    @Input() fromCustomInput: boolean = false;

    /** Utilisé pour définir le comportement du floatLabel (auto, always ou never) */
    @Input() floatLabel?: FloatLabelType = 'auto';

    /** Libellé du champ */
    @Input() label: string;

    /** Permet d'afficher du contenu après le label flottant */
    @Input() labelExtra?: TemplateRef<any>;

    /** Caractère obligatoire du champ */
    @Input() required?: boolean = false;

    /** Utilisé pour définir l'id de l'input */
    @Input() inputId: string;

    /** Caractère actif du champ */
    @Input() disabled?: boolean = false;

    /** Indique si l'input doit être vidé après la sélection */
    @Input() private isReinitAfterSelect?: boolean = false;

    /** Popup de recherche : Pas de recherche tant qu'un filtre n'a pas été renseigné */
    @Input() isSearchWhenNoFilter?: boolean;

    /** Sélection d'un élément **/
    @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();

    /** Champ de saisie */
    @ViewChild('input') input: MatInput;

    /** Observable qui permet de reset la liste des items quand un event est envoyé */
    @Input() reinitListeObs: Observable<void>;

    /** Erreur material à afficher */
    @Input() matError?: MatErrorModel;

    /** Message material à afficher */
    @Input() matHint?: MatHintModel;

    /** Contrôle */
    autocompleteControl = new FormControl<string>('');

    /** Liste des éléments */
    listeItems: Array<any>;

    /** Indicateur de chargement */
    isLoading: boolean = false;

    /** Gestion des erreurs */
    errorStateMatcher: AutocompleteErrorStateMatcher;

    /** Liste des souscriptions */
    listeSouscriptions: Array<Subscription> = new Array<Subscription>();

    /** Référence aux types de recherche pour le template */
    SearchType = SearchType;

    /** Liste des types de recherche pour l'itération dans le template */
    searchTypesList = Object.values(SearchType);

    /**
     * Constructeur
     */
    constructor(private http: HttpClient,private ngModel: NgModel,private translateService: TranslateService,
                private autocompleteService: AutocompleteService,private datePipe: DatePipe,private montantPipe: MontantPipe) {
        //Binding de l'affichage
        this.displayItem = this.displayItem.bind(this);
    }

    /**
     * Initialisation du composant
     */
    ngOnInit() {
        //Vérification des données
        if (!this.options && !this.type)
            //Déclenchement d'une exception
            throw 'Le type ou les options doivent être renseignés';

        //Lecture des options si nécessaire
        this.options = this.options || this.autocompleteService.getOptionsForType(this.type);

		//Mise à jour des options si nécessaire
		if (this.extraOptions) {
			Object.assign(this.options,this.extraOptions);
		}

        //Définition du mode de recherche
        this.options.searchType = this.options.searchType || SearchType.STARTS_WITH;

        //Mise à jour des options en fonction de l'attribut isSearchWhenNoFilter défini sur l'autocomplete
        if (this.isSearchWhenNoFilter != null) {
            this.options.isSearchWhenNoFilter = this.isSearchWhenNoFilter;
        }

        //Définition de la valeur de l'autocomplete à l'initialisation
        this.listeSouscriptions.push(this.ngModel.valueChanges.pipe(
            filter(val => val != null),
            filter(() => !this.input.focused)
        ).subscribe(value => {
            this.autocompleteControl.setValue(value)
        }));

        //Définition des règles de validation
        this.errorStateMatcher = new AutocompleteErrorStateMatcher(this.ngModel,this);

        //Filtrage des options possibles
        this.listeSouscriptions.push(this.autocompleteControl.valueChanges.pipe(
            debounceTime(500),
            filter(i => i?.length > 0),
            switchMap(value => this.performSearch(value))
        ).subscribe({
            next: listeItems =>  {
                this.listeItems = listeItems
            }
        }));

        //Définition de la valeur par défaut du placeholder
        this.placeholder = this.placeholder ?? this.translateService.instant('autocomplete.title');

        // si on a initialisé l'observable pour réinitialiser la liste
        if (this.reinitListeObs != null) {
            this.listeSouscriptions.push(
                //on souscrit à l'observable pour reset la liste
                this.reinitListeObs.subscribe(() => {
                    this.listeItems = null;
                })
            );
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Annulation des souscriptions
        this.listeSouscriptions.forEach(s => s.unsubscribe());
    }

    /**
     * Interception d'un changement de valeur
     */
    onOptionSelected(value: any) {
        //Définition du modèle lié à l'autocomplete
        this.ngModel.control.setValue(value);

        //Sélection d'un élément
        this.onSelect.emit(value);

        //Si on doit réinitialiser l'input après la sélection
        if (this.isReinitAfterSelect) {
            //On vide l'input et la liste de résultats
            this.autocompleteControl.setValue('');
            this.listeItems = null;
        }
    }

    /**
     * Vérification du changement de valeur de la saisie
     */
    onChange(event: any) {
        //Vérification de la valeur saisie
        if (!event.target.value)
            //Réinitialisation du modèle
            this.onOptionSelected(null);
    }

    /**
     * Affichage de la popup de recherche
     */
    showSearch() {
        //Affichage de la popup
        this.autocompleteService.showSearch({
            label: this.label,
            options: this.options,
            filter: this.filter
        }).subscribe({
            next: (item) => {
                //Vérification de l'élément sélectionné
                if (item) {
                    //Mise à jour de l'option
                    this.onOptionSelected(item);

                    //Mise à jour de la valeur dans l'autocomplete
                    this.autocompleteControl.setValue(item);
                }
            }
        });
    }

    /**
     * Affichage d'un élément
     */
    displayItem(item) {
        //Affichage de l'élément
        return this.options?.displayItem(item,this.translateService,this.datePipe,this.montantPipe);
    }

    /**
     * Permet de regarder l'attribut disabled afin de gérer la désactivation de l'autocomplete
     */
    ngOnChanges(changes: SimpleChanges): void {
        //Si la nouvelle valeur de l'attribut disabled est différente de la précédente on change sa valeur
        if (changes['disabled']?.previousValue != changes['disabled']?.currentValue) {
            this.disabled ? this.autocompleteControl.disable() : this.autocompleteControl.enable();
        }

        //Mise à jour des options en fonction du changement de l'attribut isSearchWhenNoFilter
        if (this.options != null && changes['isSearchWhenNoFilter']?.previousValue != changes['isSearchWhenNoFilter']?.currentValue) {
            this.options.isSearchWhenNoFilter = this.isSearchWhenNoFilter;
        }
    }

    /**
     * Remise à zéro des données
     */
    public reset() {
        //Remise à zéro de la valeur
        this.autocompleteControl.setValue(null);
    }

    /**
     * Effectue une recherche avec la valeur donnée
     */
    public performSearch(value: string): Observable<any[]> {
        //Suppression des valeurs existantes
        this.listeItems = null;

        //Indicateur de chargement
        this.isLoading = true;

        return this.http.post(environment.baseUrl+(typeof this.options.url == 'function' && this.options.url(this.filter) || this.options.url),{
            listeFilter: [{
                clef: this.options.listeFilters(this.translateService,this.filter).filter(f => f.isDefault).map(f => f.clef).join(','),
                valeur: this.options.searchType == SearchType.STARTS_WITH ? `${value}%` : `%${value}%`
            },...(this.options.listeStaticFilters?.(this.filter) || [])],
            defaultOrder: this.options.getOrderBy ? this.options.getOrderBy() : this.options.listeFilters(this.translateService,this.filter).filter(f => f.isDefault).map(f => f.clef).join(',')
        }).pipe(
            map((result: any) => this.options.mapResult && this.options.mapResult(result,true) || result.listeResultats),
            finalize(() => {
                //Fin du chargement
                this.isLoading = false;
            })
        );
    }

    /**
     * Déclenche une recherche manuelle avec la valeur actuelle de l'autocomplete
     */
    public triggerManualSearch(searchType: SearchType): void {
		//Mise à jour du mode de recherche
		this.options.searchType = searchType;

        const currentValue = this.autocompleteControl.value;
        if (currentValue && currentValue.length > 0) {
            this.performSearch(currentValue).subscribe({
                next: listeItems => {
                    this.listeItems = listeItems;
                }
            });
        }
    }
}

/**
 * Contrôle des erreurs de l'autocomplete
 */
class AutocompleteErrorStateMatcher implements ErrorStateMatcher {
    /**
     * Constructeur
     */
    constructor(private ngModel: NgModel,private autocompleteComponent: AutocompleteComponent) {
    }

    /**
     * Vérification
     */
    isErrorState(control: FormControl | null,form: FormGroupDirective | NgForm | null): boolean {
        //Vérification de la validité de la saisie
        const hasNgModelError = this.ngModel?.control?.errors != null && Object.keys(this.ngModel.control.errors).length > 0;
        const hasCustomError = !!this.autocompleteComponent?.matError?.error;

        return hasNgModelError || hasCustomError;

    }
}
